import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Page from "src/layouts/Page";
import ContentTable, {
	ContentTableColumn,
	ContentTableItem
} from "src/components/ContentTable/ContentTable";
import { ConfirmationModal } from "src/modals/ConfirmationModal/ConfirmationModal";
import useAppBrandLibrary from "src/features/AppBranding/hooks/useAppBrandLibrary/useAppBrandLibrary";
import useActiveAppBrand from "src/features/AppBranding/hooks/useActiveAppBrand/useActiveAppBrand";
import useMakeAppBrandsActive from "src/features/AppBranding/hooks/useMakeAppBrandsActive/useMakeAppBrandsActive";
import useMakeAppBrandsInactive from "src/features/AppBranding/hooks/useMakeAppBrandsInactive/useMakeAppBrandsInactive";
import useDeleteAppBrands from "src/features/AppBranding/hooks/useDeleteAppBrands/useDeleteAppBrands";
import useAdminGroups from "src/features/Groups/hooks/useAdminGroups";
import AudienceCell from "src/components/AudienceCell/AudienceCell";

interface BrandTableItem extends ContentTableItem {
	data: {
		audienceGroup: { id: number; name: string } | null;
	};
}

function mapBrandsToContentTableItems(
	activeBrand: AppBrand | Record<string, never> | undefined,
	libraryBrands: AppBrand[] | undefined
): BrandTableItem[] {
	const live: BrandTableItem[] =
		activeBrand && "id" in activeBrand
			? [
					{
						id: activeBrand.id,
						name: activeBrand.name,
						createdBy: activeBrand.createdBy
							? `${activeBrand.createdBy.firstName} ${activeBrand.createdBy.lastName}`
							: undefined,
						lastUpdated: activeBrand.updatedAt,
						isActive: true,
						order: 1,
						data: { audienceGroup: activeBrand.audienceGroup ?? null }
					}
				]
			: [];

	const draft: BrandTableItem[] = (libraryBrands ?? []).map((b) => ({
		id: b.id,
		name: b.name,
		createdBy: b.createdBy
			? `${b.createdBy.firstName} ${b.createdBy.lastName}`
			: undefined,
		lastUpdated: b.updatedAt ?? b.createdAt,
		isActive: false,
		data: { audienceGroup: b.audienceGroup ?? null }
	}));

	return [...live, ...draft];
}

const AppBranding = () => {
	const navigate = useNavigate();
	const [itemToDelete, setItemToDelete] = useState<ContentTableItem | null>(
		null
	);
	const [filterGroupId, setFilterGroupId] = useState<
		number | "all" | "everyone"
	>("all");

	const { data: activeBrand } = useActiveAppBrand();
	const { data: appBrandLibrary } = useAppBrandLibrary();
	const { data: adminGroups = [] } = useAdminGroups();

	// If the currently-selected group disappears from adminGroups (deleted by
	// another admin while this page is open), reset the filter so the user
	// isn't stuck filtered to an invisible option.
	useEffect(() => {
		if (typeof filterGroupId !== "number") return;
		if (!adminGroups.some((g) => Number(g.id) === filterGroupId)) {
			setFilterGroupId("all");
		}
	}, [adminGroups, filterGroupId]);

	const { mutate: makeBrandsActive } = useMakeAppBrandsActive();
	const { mutate: makeBrandsInactive } = useMakeAppBrandsInactive();
	const { mutate: deleteAppBrands, isLoading: isDeletingBrands } =
		useDeleteAppBrands();

	const items = useMemo(() => {
		const all = mapBrandsToContentTableItems(
			activeBrand as AppBrand | Record<string, never> | undefined,
			appBrandLibrary as AppBrand[] | undefined
		);
		if (filterGroupId === "all") return all;
		if (filterGroupId === "everyone")
			return all.filter((item) => item.data.audienceGroup === null);
		return all.filter((item) => item.data.audienceGroup?.id === filterGroupId);
	}, [activeBrand, appBrandLibrary, filterGroupId]);

	const columns = useMemo<ContentTableColumn[]>(
		() => [
			{
				key: "createdBy",
				label: "Created by",
				render: (item) => item.createdBy ?? "—"
			},
			{
				key: "lastUpdated",
				label: "Last updated",
				render: (item) => item.lastUpdated ?? "—"
			},
			{
				key: "audience",
				label: "Audience",
				render: (item) => {
					const audienceGroup =
						(
							item.data as {
								audienceGroup?: { id: number; name: string } | null;
							}
						)?.audienceGroup ?? null;
					return <AudienceCell groups={audienceGroup ? [audienceGroup] : []} />;
				}
			}
		],
		[]
	);

	const selectedFilterLabel =
		filterGroupId === "all"
			? null
			: filterGroupId === "everyone"
				? "Everyone"
				: (adminGroups.find((g) => Number(g.id) === filterGroupId)?.name ??
					"group");
	const filterAnnouncement =
		filterGroupId === "all"
			? ""
			: `Showing ${items.length} brand${items.length === 1 ? "" : "s"} filtered by ${selectedFilterLabel}`;

	return (
		<Page>
			<p className="sr-only" aria-live="polite">
				{filterAnnouncement}
			</p>
			<ContentTable
				items={items}
				columns={columns}
				showOrder={false}
				onRowClick={(item) =>
					navigate(
						`/vendor-sponsorship/app-branding/brands/new-app-branding-form/${item.id}`
					)
				}
				onMakeLive={(item) => makeBrandsActive([Number(item.id)])}
				onMakeDraft={(item) => makeBrandsInactive([Number(item.id)])}
				onDelete={(item) => setItemToDelete(item)}
				canDelete={(item) => !item.isActive}
				createPath="/vendor-sponsorship/app-branding/brands/new-app-branding-form"
				createLabel="Create Brand"
				searchRowExtra={
					<label className="flex items-center gap-2 text-sm">
						<span className="text-muted-foreground">Filter by group</span>
						<select
							value={filterGroupId}
							onChange={(e) => {
								const v = e.target.value;
								if (v === "all" || v === "everyone") setFilterGroupId(v);
								else setFilterGroupId(Number(v));
							}}
							className="rounded-lg border border-border bg-white px-3 py-2 text-sm shadow-xs focus:outline-none focus:ring-2 focus:ring-ring"
						>
							<option value="all">All groups</option>
							<option value="everyone">Everyone (no restriction)</option>
							{adminGroups.map((g) => (
								<option key={g.id} value={Number(g.id)}>
									{g.name}
								</option>
							))}
						</select>
					</label>
				}
			/>

			<ConfirmationModal
				isOpen={itemToDelete !== null}
				title="Delete brand?"
				description="This action cannot be undone."
				onCancel={() => setItemToDelete(null)}
				onSubmit={() => {
					if (itemToDelete) {
						deleteAppBrands([Number(itemToDelete.id)]);
						setItemToDelete(null);
					}
				}}
				submitBtnProps={{ isLoading: isDeletingBrands }}
			/>
		</Page>
	);
};

export default AppBranding;
